import { useEffect, useState } from "react";
import { isFtthOffer, useOffer } from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { fetchVouchers } from "@nju/data-access-portal-order";
import { usePaymentsHistory } from "@nju/data-access-portal-payment";
import { useProducts } from "@nju/data-access-portal-product";
import { useServiceStatus } from "@nju/data-access-portal-service";
import { useUser } from "@nju/data-access-portal-user";
import { useDebug } from "@nju/debug";
import {
  FtthOrderCompletedStatus,
  FtthStatusBanner,
  OrderCTABanner,
} from "@nju/feature-portal-order";
import { FtthServiceCTABanner } from "@nju/feature-portal-service";
import { CircleLoader } from "@nju/ui";

type IStates = "IDLE" | "LOADING" | "ERROR" | "DONE";

export function FtthDashboardMainPage() {
  const [currentState, setCurrentState] = useState<IStates>("IDLE");
  const [discount, setDiscount] = useState("");
  const { ftth, modemReturn, ftthReactivation } = useOrchestrators();
  const ftthOrchestratorState = ftth?.state;
  const { ftthServiceStatus } = useServiceStatus();
  const paymentsHistory = usePaymentsHistory();
  const user = useUser();
  const offer = useOffer(ftth?.offerId);
  const products = useProducts();
  const {
    sfh,
    modem,
    access,
    internet,
    hasProducts,
    hasNoProducts,
    isProductsUndefined,
    commitment,
  } = products.ftthProducts;
  const isFtthOnboardingComplete = ftthOrchestratorState === "COMPLETED";
  const isLoyaltyOffer = isFtthOffer(offer) && offer?.commitment === true;

  const payment = paymentsHistory?.payments.find(({ status }) => {
    return status === "PENDING" || status === "PENDING_REPEATING";
  });

  useDebug("SERVICE_CTA_BANNER", {
    user: user || "nu user",
    payment: payment || "no payment",
    paymentStatus: ftth?.paymentStatus || "no paymentStatus",
    ftthServiceStatus: ftthServiceStatus || "no ftthServiceStatus",
    ftthOrchestratorState: ftthOrchestratorState || "no ftthOrchestratorState",
    modemReturnOrchestrator: {
      modemSerialNumber:
        modemReturn?.modemSerialNumber || "no modemSerialNumber",
      state: modemReturn?.state || "no state",
      contractTerminationDate:
        modemReturn?.contractTerminationDate || "no contractTerminationDate",
      contractPenaltyFee:
        modemReturn?.contractPenaltyFee || "no contractPenaltyFee",
    },
    reactivationOrchestratorStatus:
      ftthReactivation?.state || "no reactivationOrchestratorStatus",
    anniversaryDate: internet?.anniversaryDate || "no anniversaryDate",
    statusChangeDate: access?.statusChangeDate || "no statusChangeDate",
    terminationDate: access?.terminationDate || "no terminationDate",
    hasProducts,
    modemProductsCharacteristics: {
      serialNumber: modem?.characteristic?.serialNumber || "no serialNumber",
    },
    willBreakLoyaltyOnResignation:
      commitment?.willBreakLoyaltyOnResignation ||
      "no willBreakLoyaltyOnResignation",
    isLoyaltyOffer,
  });

  const checkVouchers = async () => {
    setCurrentState("LOADING");

    fetchVouchers().then((result) => {
      if (result.isOk) {
        if (result?.value?.[0]?.reward?.description) {
          setDiscount(result.value[0].reward.description);
        }

        return setCurrentState("DONE");
      }

      return setCurrentState("ERROR");
    });
  };

  useEffect(() => {
    if (
      internet?.balances?.find((item) => {
        return item.balanceId === "21160";
      }) &&
      !discount &&
      currentState !== "DONE" &&
      currentState !== "ERROR"
    ) {
      checkVouchers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internet, discount]);

  /**
   * This is the only tricky part.
   * products = undefined - we did not fetch products yet
   * products = [] - there is no products
   * products = [...] - we have products
   */
  if (isProductsUndefined) {
    return (
      <div className="flex justify-center">
        <CircleLoader />
      </div>
    );
  }

  return (
    <>
      <FtthServiceCTABanner
        access={access}
        anniversaryDate={internet?.anniversaryDate}
        hasNoProducts={hasNoProducts}
        modemProducts={modem}
        modemReturnOrchestrator={modemReturn}
        orchestrator={ftth}
        payment={payment}
        reactivationOrchestratorStatus={ftthReactivation?.state}
        serviceStatus={ftthServiceStatus}
        user={user}
      />
      {hasNoProducts && !isFtthOnboardingComplete && ftth && (
        <>
          <FtthStatusBanner orchestrator={ftth} />
          <OrderCTABanner orchestrator={ftth} />
        </>
      )}
      {hasProducts && ftth && (
        <FtthOrderCompletedStatus
          access={access}
          currentState={currentState}
          discount={discount}
          ftthServiceStatus={ftthServiceStatus}
          internet={internet}
          modem={modem}
          orchestrator={ftth}
          sfh={sfh}
        />
      )}
    </>
  );
}
